import { loanGoalOptions } from '@/helpers/enums';

export const calculatePayments = (params) => {
  const {
    amount,
    interestRate,
    periodMonths,
    linkage = 0,
  } = params;

  if (!amount || !periodMonths) {
    return {
      monthlyPayment: 0,
      totalRepayment: 0,
      schedule: [],
    };
  }

  const principal = Number(amount);
  const interest = Number(interestRate) || 0;
  const months = Number(periodMonths);

  const monthlyRate = interest / 100 / 12;
  const monthlyLinkage = Number(linkage) / 100 / 12;

  let balance = principal;
  let totalRepayment = 0;

  const schedule = [];

  if (interest === 0) {
    const monthlyPayment = principal / months;

    for (let i = 0; i < months; i += 1) {
      const balanceBeforeLinkage = balance;

      balance *= 1 + monthlyLinkage;
      const linkagePayment = balance - balanceBeforeLinkage;

      balance -= monthlyPayment;

      const roundedPayment = Number(monthlyPayment.toFixed(2));
      const roundedLinkage = Number(linkagePayment.toFixed(2));
      const roundedBalance = Number(Math.max(balance, 0).toFixed(2));

      schedule.push({
        month: i + 1,
        payment: roundedPayment,
        principalPayment: roundedPayment,
        interest: 0,
        linkagePayment: roundedLinkage,
        balance: roundedBalance,
      });

      totalRepayment += roundedPayment;
    }

    return {
      monthlyPayment: schedule.length ? schedule[0].payment : 0,
      totalRepayment,
      schedule,
    };
  }

  for (let i = 0; i < months; i += 1) {
    const balanceBeforeLinkage = balance;

    balance *= 1 + monthlyLinkage;
    const linkagePayment = balance - balanceBeforeLinkage;

    const remainingMonths = months - i;

    const numerator = balance * monthlyRate * (1 + monthlyRate) ** remainingMonths;
    const denominator = (1 + monthlyRate) ** remainingMonths - 1;

    const monthlyPayment = numerator / denominator;
    const interestPayment = balance * monthlyRate;
    const principalPayment = monthlyPayment - interestPayment;

    balance -= principalPayment;

    const roundedPayment = Number(monthlyPayment.toFixed(2));
    const roundedInterest = Number(interestPayment.toFixed(2));
    const roundedPrincipal = Number(principalPayment.toFixed(2));
    const roundedLinkage = Number(linkagePayment.toFixed(2));
    const roundedBalance = Number(Math.max(balance, 0).toFixed(2));

    schedule.push({
      month: i + 1,
      payment: roundedPayment,
      principalPayment: roundedPrincipal,
      interest: roundedInterest,
      linkagePayment: roundedLinkage,
      balance: roundedBalance,
    });

    totalRepayment += roundedPayment;
  }

  return {
    monthlyPayment: schedule.length ? schedule[0].payment : 0,
    totalRepayment: totalRepayment.toFixed(0),
    schedule,
  };
};

export const calculateInterestAndLinkage = (trancheId, goal, data, period) => {
  if (!trancheId || !goal || !period) return { interest: '', linkage: '' };

  let tranche = data.find((t) => t.hebrew === trancheId);
  if (!tranche) return { interest: '', linkage: '' };

  if (tranche.conditionalTracks?.length > 0) {
    const matchingCondition = tranche.conditionalTracks.find(
      (condition) => period <= condition.maxMonths,
    );

    if (matchingCondition) {
      const conditionalTranche = data.find(
        (t) => t._id === matchingCondition.referenceTitle,
      );

      if (conditionalTranche) {
        tranche = conditionalTranche;
      }
    }
  }

  const isCycle = goal === 'cycleMortage' || goal === 'cycleGeneral';
  const goalOption = loanGoalOptions.find((option) => option.value === goal);
  let rate = tranche.rates.filter(
    (r) => r.creditPro && r.type === goalOption.more,
  );

  if (isCycle) {
    rate = rate.find((r) => r.isCycle);
  } else {
    [rate] = rate;
  }

  return { interest: rate?.rate || '', linkage: tranche?.linkage || '' };
};

export const calculateTotalsForMix = (mix) => {
  let totalLoanAmount = 0;
  let totalMonthlyPayment = 0;
  let totalInterestPayment = 0;
  let maxMonthlyPayment = 0;
  const allSchedules = [];

  mix.tranches.forEach((tranche) => {
    if (tranche.amount && tranche.interest && tranche.period) {
      const amount = parseFloat(tranche.amount) || 0;

      const calculatePaymentsObj = {
        amount,
        interestRate: tranche.interest,
        periodMonths: tranche.period,
        linkage: tranche.linkage,
      };

      const { monthlyPayment, schedule } = calculatePayments(calculatePaymentsObj);
      totalLoanAmount += amount;
      totalMonthlyPayment += parseFloat(monthlyPayment) || 0;
      if (schedule && schedule.length > 0) allSchedules.push(schedule);
      if (schedule && schedule.length > 0) {
        totalInterestPayment += schedule.reduce(
          (sum, row) => sum + parseFloat(row.interest),
          0,
        );
      }
    }
  });

  if (allSchedules.length > 0) {
    const maxMonths = Math.max(...allSchedules.map((s) => s.length));
    for (let month = 0; month < maxMonths; month += 1) {
      let monthTotal = 0;
      allSchedules.forEach((schedule) => {
        if (month < schedule.length) monthTotal += parseFloat(schedule[month].payment);
      });
      maxMonthlyPayment = Math.max(maxMonthlyPayment, monthTotal);
    }
  }

  return {
    totalLoanAmount: totalLoanAmount.toFixed(0),
    totalMonthlyPayment: totalMonthlyPayment.toFixed(0),
    totalInterestPayment: totalInterestPayment.toFixed(0),
    maxMonthlyPayment: maxMonthlyPayment.toFixed(0),
  };
};

export const calculateMixTotals = (mix) => {
  let totalLoanAmount = 0;
  let totalMonthlyPayment = 0;
  let totalInterestPayment = 0;
  let totalLinkagePayment = 0;
  let maxMonthlyPayment = 0;
  const allSchedules = [];

  mix.tranches.forEach((tranche) => {
    if (tranche.amount && tranche.interest && tranche.period) {
      const amount = parseFloat(tranche.amount) || 0;
      const calculatePaymentsObj = {
        amount,
        interestRate: tranche.interest,
        periodMonths: tranche.period,
        linkage: tranche.linkage,
      };

      const { monthlyPayment, schedule } = calculatePayments(calculatePaymentsObj);

      totalLoanAmount += amount;
      totalMonthlyPayment += parseFloat(monthlyPayment) || 0;

      if (schedule && schedule.length > 0) {
        const totalInterest = schedule.reduce((sum, row) => sum + parseFloat(row.interest), 0);
        // eslint-disable-next-line max-len
        const totalLinkage = schedule.reduce((sum, row) => sum + parseFloat(row.linkagePayment || 0), 0);
        totalInterestPayment += totalInterest;
        totalLinkagePayment += totalLinkage;
        allSchedules.push(schedule);
      }
    }
  });

  if (allSchedules.length > 0) {
    const maxMonths = Math.max(...allSchedules.map((schedule) => schedule.length));

    for (let month = 0; month < maxMonths; month += 1) {
      let monthTotal = 0;

      allSchedules.forEach((schedule) => {
        if (month < schedule.length) {
          monthTotal += parseFloat(schedule[month].payment);
        }
      });

      maxMonthlyPayment = Math.max(maxMonthlyPayment, monthTotal);
    }
  }

  const totalRepayment = totalLoanAmount + totalInterestPayment + totalLinkagePayment;
  const repaymentPerShekel = totalRepayment / totalLoanAmount;

  return {
    totalLoanAmount: totalLoanAmount.toFixed(0),
    totalMonthlyPayment: totalMonthlyPayment.toFixed(0),
    totalInterestPayment: totalInterestPayment.toFixed(0),
    totalLinkagePayment: totalLinkagePayment.toFixed(0),
    totalRepayment: totalRepayment.toFixed(0),
    repaymentPerShekel: repaymentPerShekel.toFixed(2),
    maxMonthlyPayment: maxMonthlyPayment.toFixed(0),
    allSchedules,
  };
};
